import { HostInfo, HostMetadataValue, HostPlatform, HostType } from "./types";

const PLATFORMS: HostPlatform[] = ["android", "windows", "unknown"];
const HOST_TYPES: HostType[] = ["rn", "cefsharp", "browser"];

const asObject = (value: unknown): Record<string, any> =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, any>) : {};

const asString = (value: unknown): string => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && isFinite(value)) return String(value);
  return "";
};

const asOptionalString = (value: unknown): string | undefined => asString(value) || undefined;

const asOptionalNumber = (value: unknown): number | undefined => {
  const parsed = typeof value === "string" ? Number(value) : value;
  return typeof parsed === "number" && isFinite(parsed) ? parsed : undefined;
};

const asMetadata = (value: unknown): Record<string, HostMetadataValue> => {
  const result: Record<string, HostMetadataValue> = {};
  Object.entries(asObject(value)).forEach(([key, item]) => {
    if (typeof item === "string" || typeof item === "boolean" || (typeof item === "number" && isFinite(item))) {
      result[key] = item;
    }
  });
  return result;
};

const pick = <T extends string>(value: unknown, allowed: T[], fallback: T): T => {
  const text = asString(value).toLowerCase() as T;
  return allowed.indexOf(text) >= 0 ? text : fallback;
};

/**
 * Converte o que o host mandou (qualquer formato, inclusive string JSON ou nada)
 * num HostInfo completo. Nunca lança erro.
 */
export const normalizeHostInfo = (raw: unknown, source: HostInfo["source"] = "none"): HostInfo => {
  let input: unknown = raw;
  if (typeof input === "string") {
    try {
      input = JSON.parse(input);
    } catch (error) {
      input = {};
    }
  }

  const data = asObject(input);
  const host = asObject(data.host);
  const os = asObject(data.os);
  const device = asObject(data.device);

  return {
    schemaVersion: asOptionalNumber(data.schemaVersion) ?? 1,
    platform: pick(data.platform, PLATFORMS, "unknown"),
    host: {
      type: pick(host.type, HOST_TYPES, "browser"),
      version: asString(host.version),
    },
    os: {
      name: asString(os.name),
      version: asString(os.version),
      build: asOptionalString(os.build),
      apiLevel: asOptionalNumber(os.apiLevel),
    },
    device: {
      manufacturer: asString(device.manufacturer),
      model: asString(device.model),
      family: asString(device.family).toLowerCase() || "unknown",
      serial: asOptionalString(device.serial),
      macAddress: asOptionalString(device.macAddress),
    },
    metadata: asMetadata(data.metadata),
    source,
  };
};

export const UNKNOWN_HOST: HostInfo = normalizeHostInfo({}, "none");
